/**
 * @fileoverview Accessibility Context for managing application-wide accessibility features and settings.
 *
 * This context provides comprehensive accessibility functionality including:
 * - Visual accessibility settings (high contrast, large text, dyslexic font)
 * - Motion preferences (reduced motion, animations)
 * - Focus and navigation enhancements
 * - Reading assistance tools (reading guide, screen reader support)
 * - Cursor and zoom customization
 * - Persistent settings storage in localStorage
 *
 * Used throughout the application for:
 * - Accessibility modal and settings panel
 * - Dynamic CSS class application for accessibility features
 * - Font size and color contrast adjustments
 * - Motion and animation control
 * - Reading and navigation assistance
 */

import React, { createContext, useContext, useState, useEffect } from 'react';

/**
 * React Context for accessibility state and methods
 * @type {React.Context<AccessibilityContextValue>}
 */
const AccessibilityContext = createContext();

/**
 * Custom hook to access the accessibility context.
 * Must be used within an AccessibilityProvider component.
 *
 * @function useAccessibility
 * @returns {AccessibilityContextValue} The accessibility context value containing settings and controls
 * @throws {Error} When used outside of AccessibilityProvider
 *
 * @example
 * ```jsx
 * function AccessibilityButton() {
 *   const { toggleModal, highContrast, toggleHighContrast } = useAccessibility();
 *
 *   return (
 *     <button onClick={toggleModal}>
 *       Accessibility Settings
 *     </button>
 *   );
 * }
 * ```
 */
export const useAccessibility = () => {
  const context = useContext(AccessibilityContext);
  if (!context) {
    throw new Error('useAccessibility must be used within an AccessibilityProvider');
  }
  return context;
};

/**
 * Accessibility Provider component that manages accessibility settings and provides
 * accessibility controls to child components.
 *
 * @component
 * @param {Object} props - Component props
 * @param {React.ReactNode} props.children - Child components that will have access to accessibility context
 * @returns {JSX.Element} Provider component wrapping children with accessibility context
 *
 * @example
 * ```jsx
 * function App() {
 *   return (
 *     <AccessibilityProvider>
 *       <Header />
 *       <MainContent />
 *       <AccessibilityModal />
 *     </AccessibilityProvider>
 *   );
 * }
 * ```
 */
export const AccessibilityProvider = ({ children }) => {
  // Modal visibility
  const [isModalOpen, setIsModalOpen] = useState(false);

  // Visual accessibility settings
  const [highContrast, setHighContrast] = useState(false);
  const [largeText, setLargeText] = useState(false);
  const [dyslexicFont, setDyslexicFont] = useState(false);
  const [reducedMotion, setReducedMotion] = useState(false);
  const [focusIndicators, setFocusIndicators] = useState(false);


  // Reading accessibility
  const [readingGuide, setReadingGuide] = useState(false);
  const [screenReader, setScreenReader] = useState(false);
  const [cursorSize, setCursorSize] = useState('normal'); // small, normal, large, extra-large

  // Zoom and magnification
  const [zoomLevel, setZoomLevel] = useState(100);
  const [magnifierEnabled, setMagnifierEnabled] = useState(false);

  // Load settings from localStorage on mount
  useEffect(() => {
    const savedSettings = localStorage.getItem('accessibilitySettings');
    if (savedSettings) {
      try {
        const settings = JSON.parse(savedSettings);
        setHighContrast(settings.highContrast || false);
        setLargeText(settings.largeText || false);
        setDyslexicFont(settings.dyslexicFont || false);
        setReducedMotion(settings.reducedMotion || false);
        setFocusIndicators(settings.focusIndicators || false);
        setReadingGuide(settings.readingGuide || false);
        setScreenReader(settings.screenReader || false);
        setCursorSize(settings.cursorSize || 'normal');
        setZoomLevel(settings.zoomLevel || 100);
        setMagnifierEnabled(settings.magnifierEnabled || false);
      } catch (error) {
        if (process.env.NODE_ENV === 'development') {
          console.error('Failed to parse accessibility settings:', error);
        }
      }
    }
  }, []);

  /**
   * Saves accessibility settings to localStorage
   * @param {Object} newSettings - New settings to merge with current settings
   */
  const saveSettings = (newSettings) => {
    const settings = {
      highContrast,
      largeText,
      dyslexicFont,
      reducedMotion,
      focusIndicators,
      readingGuide,
      screenReader,
      cursorSize,
      zoomLevel,
      magnifierEnabled,
      ...newSettings
    };
    localStorage.setItem('accessibilitySettings', JSON.stringify(settings));
  };

  // Apply CSS classes to body based on settings
  useEffect(() => {
    const bodyClasses = [];
    
    if (highContrast) bodyClasses.push('accessibility-high-contrast');
    if (largeText) bodyClasses.push('accessibility-large-text');
    if (dyslexicFont) bodyClasses.push('accessibility-dyslexic-font');
    if (reducedMotion) bodyClasses.push('accessibility-reduced-motion');
    if (focusIndicators) bodyClasses.push('accessibility-focus-indicators');
    if (readingGuide) bodyClasses.push('accessibility-reading-guide');
    if (cursorSize !== 'normal') bodyClasses.push(`accessibility-cursor-${cursorSize}`);
    
    // Remove existing accessibility classes
    document.body.classList.forEach(className => {
      if (className.startsWith('accessibility-')) {
        document.body.classList.remove(className);
      }
    });
    
    // Add current accessibility classes
    bodyClasses.forEach(className => {
      document.body.classList.add(className);
    });
    
    // Apply zoom level
    if (zoomLevel !== 100) {
      document.documentElement.style.fontSize = `${zoomLevel}%`;
    } else {
      document.documentElement.style.fontSize = '';
    }
  }, [highContrast, largeText, dyslexicFont, reducedMotion, focusIndicators, readingGuide, cursorSize, zoomLevel]);

  // Helper functions for modal control
  /** Toggles the accessibility modal open/closed */
  const toggleModal = () => setIsModalOpen(!isModalOpen);

  /** Closes the accessibility modal */
  const closeModal = () => setIsModalOpen(false);

  /** Opens the accessibility modal */
  const openModal = () => setIsModalOpen(true);

  /** Toggles high contrast mode and saves setting */
  const toggleHighContrast = () => {
    const newValue = !highContrast;
    setHighContrast(newValue);
    saveSettings({ highContrast: newValue });
  };

  /** Toggles large text mode and saves setting */
  const toggleLargeText = () => {
    const newValue = !largeText;
    setLargeText(newValue);
    saveSettings({ largeText: newValue });
  };

  /** Toggles dyslexic-friendly font and saves setting */
  const toggleDyslexicFont = () => {
    const newValue = !dyslexicFont;
    setDyslexicFont(newValue);
    saveSettings({ dyslexicFont: newValue });
  };

  /** Toggles reduced motion mode and saves setting */
  const toggleReducedMotion = () => {
    const newValue = !reducedMotion;
    setReducedMotion(newValue);
    saveSettings({ reducedMotion: newValue });
  };

  /** Toggles enhanced focus indicators and saves setting */
  const toggleFocusIndicators = () => {
    const newValue = !focusIndicators;
    setFocusIndicators(newValue);
    saveSettings({ focusIndicators: newValue });
  };

  /** Toggles reading guide and saves setting */
  const toggleReadingGuide = () => {
    const newValue = !readingGuide;
    setReadingGuide(newValue);
    saveSettings({ readingGuide: newValue });
  };

  /**
   * Sets cursor size and saves setting
   * @param {'small'|'normal'|'large'|'extra-large'} size - The cursor size
   */
  const setCursor = (size) => {
    setCursorSize(size);
    saveSettings({ cursorSize: size });
  };

  /**
   * Sets zoom level and saves setting
   * @param {number} level - The zoom level percentage (e.g., 100, 125, 150)
   */
  const setZoom = (level) => {
    setZoomLevel(level);
    saveSettings({ zoomLevel: level });
  };

  /** Resets all accessibility settings to default values and clears localStorage */
  const resetAllSettings = () => {
    setHighContrast(false);
    setLargeText(false);
    setDyslexicFont(false);
    setReducedMotion(false);
    setFocusIndicators(false);
    setReadingGuide(false);
    setScreenReader(false);
    setCursorSize('normal');
    setZoomLevel(100);
    setMagnifierEnabled(false);
    localStorage.removeItem('accessibilitySettings');
  };

  /**
   * The context value object containing all accessibility state and methods
   * @typedef {Object} AccessibilityContextValue
   * @property {boolean} isModalOpen - Whether the accessibility modal is open
   * @property {Function} toggleModal - Toggle accessibility modal visibility
   * @property {Function} closeModal - Close accessibility modal
   * @property {Function} openModal - Open accessibility modal
   * @property {boolean} highContrast - High contrast mode enabled
   * @property {boolean} largeText - Large text mode enabled
   * @property {boolean} dyslexicFont - Dyslexic-friendly font enabled
   * @property {boolean} reducedMotion - Reduced motion mode enabled
   * @property {boolean} focusIndicators - Enhanced focus indicators enabled
   * @property {boolean} readingGuide - Reading guide enabled
   * @property {boolean} screenReader - Screen reader mode enabled
   * @property {string} cursorSize - Current cursor size ('small'|'normal'|'large'|'extra-large')
   * @property {number} zoomLevel - Current zoom level percentage
   * @property {boolean} magnifierEnabled - Screen magnifier enabled
   * @property {Function} toggleHighContrast - Toggle high contrast mode
   * @property {Function} toggleLargeText - Toggle large text mode
   * @property {Function} toggleDyslexicFont - Toggle dyslexic font
   * @property {Function} toggleReducedMotion - Toggle reduced motion
   * @property {Function} toggleFocusIndicators - Toggle focus indicators
   * @property {Function} toggleReadingGuide - Toggle reading guide
   * @property {Function} setCursor - Set cursor size
   * @property {Function} setZoom - Set zoom level
   * @property {Function} resetAllSettings - Reset all settings to defaults
   */
  const value = {
    // Modal state
    isModalOpen,
    toggleModal,
    closeModal,
    openModal,

    // Settings state
    highContrast,
    largeText,
    dyslexicFont,
    reducedMotion,
    focusIndicators,
    readingGuide,
    screenReader,
    cursorSize,
    zoomLevel,
    magnifierEnabled,

    // Actions
    toggleHighContrast,
    toggleLargeText,
    toggleDyslexicFont,
    toggleReducedMotion,
    toggleFocusIndicators,
    toggleReadingGuide,
    setCursor,
    setZoom,
    resetAllSettings
  };

  return (
    <AccessibilityContext.Provider value={value}>
      {children}
    </AccessibilityContext.Provider>
  );
};